import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Globe,
  Lock,
  CheckCircle,
  Captions,
  CaptionsOff,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import GradeSelector from "@features/tools/components/grade-selector";
import CourseSelector from "@features/assignments/AssignmentGenerator/components/course-selector";
import {
  BaseChatDtoCourse,
  GetTopicsCourseType,
  VideoAvatarCreateRequestDtoGenerationType,
  VideoAvatarCreateRequestDto,
} from "@packages/api/madlenAPI.schemas";
import { Button } from "@core/button";
import { firstTopicOfCourse, getEnvironment } from "@packages/utils";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import { useToast } from "@packages/contexts/ToastContext";
import CourseAndAudianceInput, {
  processPrompt,
} from "@features/tools/components/create-modal/CourseAndAudianceInput";
import { CourseData } from "@packages/constant/CourseData";
import AvatarSelector from "@features/tools/components/tool_components/AvatarSelector";
import { useGetAvailableVideoAvatarFaces } from "@packages/api/madlenAPI";

interface CreateHeygenVideoViewProps {
  onSubmit?: (input: VideoAvatarCreateRequestDto) => Promise<void>;
  isLoading?: boolean;
  initialInput?: VideoAvatarCreateRequestDto;
  placeholder?: string;
}

const CreateHeygenVideoView = ({
  onSubmit = async () => {},
  isLoading = false,
  initialInput = {
    courseName: "UNKNOWN",
    gradeLevel: 9,
    prompt: "",
    environment: getEnvironment(),
    isPublic: true,
    generationType: VideoAvatarCreateRequestDtoGenerationType.PROMPT,
    requestAvatar: undefined,
  } as VideoAvatarCreateRequestDto,
  placeholder,
}: CreateHeygenVideoViewProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [input, setInput] = useState<VideoAvatarCreateRequestDto>({
    ...initialInput,
  });
  const [isTopicSelection, setIsTopicSelection] = useState(false);
  const [isOutcomeSelection, setIsOutcomeSelection] = useState(
    i18n.language === "tr",
  );
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const location = useLocation();
  const [selectedMaarifOutcomes, setSelectedMaarifOutcomes] = useState<
    number[]
  >([]);
  const [isMaarif, setIsMaarif] = useState(() => {
    const savedValue = localStorage.getItem("isMaarifEnabled");
    return savedValue
      ? JSON.parse(savedValue)
      : (location.state?.isMaarif ?? false);
  });

  // Maarif durumu localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem("isMaarifEnabled", JSON.stringify(isMaarif));
  }, [isMaarif]);
  // Ensure grade level is treated as a number for calculations
  const gradeAsNumber = Number(input.gradeLevel);

  // Check if selected grade is in 1-4 range
  const isJuniorGrade = gradeAsNumber >= -1 && gradeAsNumber <= 4;

  const placeholderTopic = useMemo(() => {
    return firstTopicOfCourse(
      Number(input.gradeLevel),
      input.courseName as GetTopicsCourseType,
    );
  }, [input.gradeLevel, input.courseName]);
  const { data: availableVideoAvatarFaces } = useGetAvailableVideoAvatarFaces();

  // Step 1: Validate avatar selection
  const validateFirstStep = (): boolean => {
    if (!input.requestAvatar || !input.requestAvatar.avatarId) {
      addToast({
        title: t("heygenVideoGenerator.avatarEmpty"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  // Step 2: Validate topic selection
  const validateSecondStep = (): boolean => {
    if (isTopicSelection && selectedTopics.length === 0) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    // Validate outcome selection
    if (
      isOutcomeSelection &&
      selectedOutcomes.length === 0 &&
      selectedMaarifOutcomes.length === 0
    ) {
      addToast({
        title: t("notifications.assignment.emptyOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    if (!isTopicSelection && !isOutcomeSelection && !input.prompt?.trim()) {
      addToast({
        title: t("notifications.assignment.emptyPrompt"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  // Step navigation handlers
  const handleContinueToTopicStep = () => {
    if (validateFirstStep()) {
      setCurrentStep(2);
    }
  };

  const handleBackToAvatarStep = () => {
    setCurrentStep(1);
  };

  // Submission handler (now only on step 2)
  const handleSubmit = async () => {
    if (!validateSecondStep()) return;
    try {
      const { captionEnabled, ...dto } = input;
      await onSubmit({ ...dto, captionEnabled });
    } catch (error) {
      console.error("Error submitting video:", error);
    }
  };

  const handleGradeChange = (grade: number) => {
    const availableCourses = Object.entries(CourseData).filter(([, value]) =>
      value.visible(grade),
    );
    const firstAvailableCourse =
      availableCourses[0]?.[0] as GetTopicsCourseType;
    const doesPreviousCourseExist = availableCourses.some(
      ([key]) => key === input.courseName,
    );
    setSelectedTopics([]);
    setSelectedOutcomes([]);
    setSelectedMaarifOutcomes([]);

    // If switching to a junior grade (1-4), force PROMPT mode
    const isNewGradeJunior = grade >= 1 && grade <= 4;
    if (isNewGradeJunior || !firstAvailableCourse) {
      setIsTopicSelection(false);
      setIsOutcomeSelection((prev) => {
        return prev && firstAvailableCourse ? i18n.language === "tr" : false;
      });
    }

    setInput({
      ...input,
      gradeLevel: grade,
      courseName: doesPreviousCourseExist
        ? input.courseName
        : (firstAvailableCourse ?? "UNKNOWN"),
      generationType:
        isNewGradeJunior || !firstAvailableCourse
          ? VideoAvatarCreateRequestDtoGenerationType.PROMPT
          : input.generationType,
    });
  };

  useEffect(() => {
    setInput((prev) => {
      return {
        ...prev,
        courseName: (i18n.language === "tr"
          ? "HISTORY"
          : "AP_2025_WORLD_HISTORY_MODERN") as BaseChatDtoCourse,
        prompt: "",
      };
    });
    setSelectedTopics([]);
    setSelectedOutcomes([]);
    setIsOutcomeSelection(i18n.language === "tr");
  }, [i18n.language]);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/content/video-avatar-generator");
  };

  const gradientClasses =
    "from-purple-500 to-violet-600 dark:from-purple-400 dark:to-violet-500";
  const submitGradientClasses =
    "from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700";

  const labelClasses = "text-purple-700 dark:text-purple-300";

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-900 dark:to-purple-900`}
    >
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all animate-scale">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t("heygenVideoGenerator.success.title")}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t("heygenVideoGenerator.success.description")}
              </p>
              <Button
                onClick={handleSuccessModalClose}
                variant="solid"
                size="lg"
                rounded="xl"
                textColor="text-white"
                bgColor={`bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700`}
                className="w-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-purple-500/50"
              >
                {t("common.ok")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container max-w-7xl mx-auto px-4 py-4 relative">
        <Button
          onClick={() => navigate("/content/video-avatar-generator")}
          variant="solid"
          size="md"
          textColor="text-white dark:text-white"
          bgColor={
            "bg-purple-500 dark:bg-purple-600 hover:bg-purple-600 dark:hover:bg-purple-700"
          }
          rounded="lg"
          className="mb-2"
        >
          <ArrowLeft size={24} />
        </Button>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2 flex flex-col items-start">
                  <h4
                    className={`inline-block text-base font-medium ${labelClasses} text-center w-full`}
                  >
                    {t("heygenVideoGenerator.title")}
                  </h4>

                  {/* Step indicators */}
                  <div className="w-full flex justify-center mb-4">
                    <div className="flex items-center space-x-2">
                      <div
                        className={`h-3 w-3 rounded-full ${
                          currentStep === 1
                            ? "bg-purple-600"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div className="h-0.5 w-8 bg-gray-300 dark:bg-gray-600" />
                      <div
                        className={`h-3 w-3 rounded-full ${
                          currentStep === 2
                            ? "bg-purple-600"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                    </div>
                  </div>

                  {currentStep === 1 && (
                    <>
                      <div className="space-y-4 mt-4 w-full">
                        <label
                          className={`inline-block text-sm font-medium ${labelClasses}`}
                        >
                          {t("heygenVideoGenerator.selectAvatar")}
                        </label>
                        <AvatarSelector
                          input={input}
                          setInput={(input) =>
                            setInput(input as VideoAvatarCreateRequestDto)
                          }
                          avatars={
                            availableVideoAvatarFaces?.filter((face) =>
                              face.language?.startsWith(i18n.language),
                            ) ?? []
                          }
                        />
                      </div>
                      <div className="flex justify-end mt-6 w-full">
                        <Button
                          onClick={handleContinueToTopicStep}
                          variant="solid"
                          size="lg"
                          rounded="xl"
                          textColor="text-white"
                          bgColor={`bg-gradient-to-r ${submitGradientClasses}`}
                          className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-purple-500/50"
                          rightIcon={<ArrowRight size={20} />}
                        >
                          {t("heygenVideoGenerator.continueAvatar")}
                        </Button>
                      </div>
                    </>
                  )}

                  {currentStep === 2 && (
                    <div className="space-y-4 mt-4 w-full">
                      <label
                        className={`inline-block text-sm font-medium ${labelClasses}`}
                      >
                        {t("hooks.form.gradeLevel")}
                      </label>
                      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="grid grid-cols-4 gap-3">
                          <GradeSelector
                            selectedGrade={Number(input.gradeLevel)}
                            setSelectedGrade={handleGradeChange}
                          />
                        </div>
                        <div className="flex flex-col items-center gap-4 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-4">
                              <Button
                                type="button"
                                onClick={() =>
                                  setInput({ ...input, isPublic: true })
                                }
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  input.isPublic
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  input.isPublic
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }
                                leftIcon={<Globe size={16} />}
                                className="w-36"
                              >
                                <span className="text-sm font-medium">
                                  {t("hooks.form.public")}
                                </span>
                              </Button>

                              <Button
                                type="button"
                                onClick={() =>
                                  setInput({ ...input, isPublic: false })
                                }
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  !input.isPublic
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  !input.isPublic
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }
                                className="w-36"
                                leftIcon={<Lock size={16} />}
                              >
                                <span className="text-sm font-medium">
                                  {t("hooks.form.private")}
                                </span>
                              </Button>
                            </div>
                          </div>
                          <div className="flex flex-col w-full">
                            <div className="flex items-center justify-center gap-4 w-full">
                              <Button
                                type="button"
                                onClick={() =>
                                  setInput({ ...input, captionEnabled: true })
                                }
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  input.captionEnabled
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  input.captionEnabled
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }
                                className="w-36"
                                leftIcon={<Captions size={16} />}
                              >
                                <span className="text-sm font-medium">
                                  {t("heygenVideoGenerator.captions")}{" "}
                                  {t("common.enable")}
                                </span>
                              </Button>
                              <Button
                                type="button"
                                onClick={() =>
                                  setInput({ ...input, captionEnabled: false })
                                }
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  !input.captionEnabled
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  !input.captionEnabled
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }
                                className="w-36"
                                leftIcon={<CaptionsOff size={16} />}
                              >
                                <span className="text-sm font-medium">
                                  {t("heygenVideoGenerator.captions")}{" "}
                                  {t("common.disable")}
                                </span>
                              </Button>
                            </div>
                          </div>
                          {/* Maarif Toggle Switch */}
                          {i18n.language === "tr" &&
                            gradeAsNumber !== -1 &&
                            gradeAsNumber > 4 && (
                              <div className="flex flex-col items-center justify-center mt-4 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                                <span className="block w-full text-sm font-medium text-gray-900 dark:text-gray-300 text-center mb-2">
                                  Türkiye Yüzyılı Maarif Modeli
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isMaarif}
                                    onChange={(e) => {
                                      setIsMaarif(e.target.checked);
                                      // Clear outcomes when Maarif changes
                                      setSelectedOutcomes([]);
                                      setSelectedMaarifOutcomes([]);
                                    }}
                                    className="sr-only peer"
                                  />
                                  <div
                                    className={`w-11 h-6 rounded-full peer relative transition-colors duration-300
                                ${isMaarif ? "bg-blue-600 dark:bg-blue-600" : "bg-gray-200 dark:bg-gray-700"}
                                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800`}
                                  ></div>
                                  <div
                                    className={`absolute h-5 w-5 rounded-full bg-white border border-gray-300 
                                transition-transform duration-300 top-[2px] start-[2px]
                                ${isMaarif ? "translate-x-5 rtl:-translate-x-5" : ""}`}
                                  ></div>
                                  <span className="ms-3 text-xs text-gray-600 dark:text-gray-400">
                                    {isMaarif ? "Açık" : "Kapalı"}
                                  </span>
                                </label>
                              </div>
                            )}
                        </div>
                      </div>
                      {/* Only show course selection if grade is not 1-4 */}
                      {gradeAsNumber !== -1 && (
                        <div className="space-y-2 flex flex-col items-start">
                          <label
                            className={`inline-block text-sm font-medium ${labelClasses}`}
                          >
                            {t("hooks.form.courseName")}
                          </label>
                          <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-3">
                            <CourseSelector
                              selectedGrade={Number(input.gradeLevel)}
                              filteredCourses={
                                isOutcomeSelection
                                  ? isMaarif
                                    ? [GetTopicsCourseType.DIGITAL_CITIZENSHIP]
                                    : [
                                        GetTopicsCourseType.ENGLISH,
                                        GetTopicsCourseType.DIGITAL_CITIZENSHIP,
                                      ]
                                  : []
                              }
                              selectedCourse={
                                input.courseName as GetTopicsCourseType
                              }
                              setSelectedCourse={(course) => {
                                setSelectedTopics([]);
                                setSelectedOutcomes([]);
                                setSelectedMaarifOutcomes([]);

                                setInput({
                                  ...input,
                                  courseName: course,
                                });
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {gradeAsNumber === -1 && (
                        <CourseAndAudianceInput
                          input={input}
                          setInput={setInput}
                          labelClass={labelClasses}
                        />
                      )}
                    </div>
                  )}
                </div>

                {currentStep === 2 && (
                  <div className="space-y-4 mt-4 w-full">
                    <div className="space-y-4">
                      {/* Only show selection mode buttons if grade is not 1-4 */}
                      {gradeAsNumber !== -1 && (
                        <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4">
                          <Button
                            type="button"
                            className="flex-1"
                            onClick={() => {
                              setIsTopicSelection(false);
                              setIsOutcomeSelection(false);
                              setInput({
                                ...input,
                                generationType:
                                  VideoAvatarCreateRequestDtoGenerationType.PROMPT,
                                prompt: "", // Reset lesson topic when switching modes
                              });
                              setSelectedTopics([]); // Reset selected topics
                              setSelectedOutcomes([]); // Reset selected outcomes
                            }}
                            variant="solid"
                            size="md"
                            rounded="lg"
                            textColor={
                              !isTopicSelection && !isOutcomeSelection
                                ? "text-white"
                                : "text-gray-600 dark:text-gray-400"
                            }
                            bgColor={
                              !isTopicSelection && !isOutcomeSelection
                                ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                : "bg-gray-100 dark:bg-gray-700"
                            }
                          >
                            {t("common.freeInput")}
                          </Button>
                          {!isJuniorGrade ? (
                            <>
                              <div className="flex items-center justify-center">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {t("common.or")}
                                </div>
                              </div>
                              <Button
                                type="button"
                                className="flex-1"
                                onClick={() => {
                                  setIsTopicSelection(true);
                                  setIsOutcomeSelection(false);
                                  setInput({
                                    ...input,
                                    generationType:
                                      VideoAvatarCreateRequestDtoGenerationType.TOPIC_SELECTION,
                                    prompt: "", // Reset lesson topic when switching modes
                                  });
                                  setSelectedOutcomes([]);
                                }}
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  isTopicSelection
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  isTopicSelection
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700"
                                }
                              >
                                {t("common.topicSelection")}
                              </Button>
                            </>
                          ) : null}
                          {i18n.language !== "en" && (
                            <>
                              <div className="flex items-center justify-center">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {t("common.or")}
                                </div>
                              </div>
                              <Button
                                type="button"
                                className={isJuniorGrade ? "flex-1" : ""}
                                onClick={() => {
                                  setIsTopicSelection(false);
                                  setIsOutcomeSelection(true);
                                  const availableCourses = Object.entries(
                                    CourseData,
                                  ).filter(
                                    ([, value]) =>
                                      value.visible(gradeAsNumber) &&
                                      value.hasMaarifOutcomes,
                                  );
                                  const firstAvailableCourse =
                                    availableCourses[0]?.[0] as GetTopicsCourseType;
                                  const doesPreviousCourseExist =
                                    availableCourses.some(
                                      ([key]) => key === input.courseName,
                                    );
                                  setInput({
                                    ...input,
                                    generationType:
                                      VideoAvatarCreateRequestDtoGenerationType.MAARIF_LEARNING_OUTCOME,
                                    courseName: doesPreviousCourseExist
                                      ? input.courseName
                                      : (firstAvailableCourse ?? "UNKNOWN"), // Reset lesson topic when switching
                                    prompt: "", // Reset lesson topic when switching modes
                                  });
                                  setSelectedTopics([]);
                                }}
                                variant="solid"
                                size="md"
                                rounded="lg"
                                textColor={
                                  isOutcomeSelection
                                    ? "text-white"
                                    : "text-gray-600 dark:text-gray-400"
                                }
                                bgColor={
                                  isOutcomeSelection
                                    ? `bg-gradient-to-r ${gradientClasses} shadow-lg`
                                    : "bg-gray-100 dark:bg-gray-700"
                                }
                              >
                                {isMaarif || gradeAsNumber < 5
                                  ? t("common.maarifOutcomeSelection")
                                  : t("common.outcomeSelection")}
                              </Button>
                            </>
                          )}
                        </div>
                      )}

                      <div className="space-y-4">
                        {isTopicSelection && !isJuniorGrade ? (
                          <UnitTopicSelector
                            grade={Number(input.gradeLevel)}
                            courseType={input.courseName as GetTopicsCourseType}
                            selectedTopics={selectedTopics}
                            setSelectedTopics={(topicIds, topicNames) => {
                              setSelectedTopics(topicIds);
                              setInput({
                                ...input,
                                prompt: topicNames ? topicNames.join(", ") : "",
                              });
                            }}
                          />
                        ) : isOutcomeSelection && gradeAsNumber !== -1 ? (
                          <UnitOutcomeSelector
                            grade={Number(input.gradeLevel)}
                            courseType={input.courseName as GetTopicsCourseType}
                            selectedOutcomes={selectedOutcomes}
                            isMaarif={isMaarif || gradeAsNumber < 5}
                            setSelectedOutcomes={(outcomeIds, outcomeTexts) => {
                              setSelectedOutcomes(outcomeIds);
                              setInput({
                                ...input,
                                prompt: outcomeTexts
                                  ? outcomeTexts.join(", ")
                                  : "",
                              });
                            }}
                          />
                        ) : (
                          <div className="space-y-2">
                            <label
                              className={`inline-block text-sm font-medium ${labelClasses}`}
                            >
                              {isJuniorGrade
                                ? t("hooks.form.promptJunior")
                                : t("hooks.form.lessonTopic")}
                            </label>
                            <input
                              type="text"
                              data-testid="lesson-topic-input"
                              value={
                                gradeAsNumber === -1 ? undefined : input.prompt
                              }
                              onChange={(e) => {
                                if (gradeAsNumber === -1) {
                                  const newPrompt = processPrompt(
                                    e.target.value,
                                    input.prompt ?? "",
                                    i18n.language,
                                  );
                                  setInput({ ...input, prompt: newPrompt });
                                } else {
                                  setInput({
                                    ...input,
                                    prompt: e.target.value,
                                  });
                                }
                              }}
                              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
                              placeholder={
                                isJuniorGrade
                                  ? t("hooks.form.promptJuniorPlaceholder")
                                  : placeholder ||
                                    (i18n.language === "tr"
                                      ? "Örnek: "
                                      : "Example: ") + placeholderTopic?.topic
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between mt-4 w-full">
                      <Button
                        onClick={handleBackToAvatarStep}
                        variant="outline"
                        size="lg"
                        rounded="xl"
                        textColor="text-purple-600 dark:text-purple-400"
                        borderColor="border-purple-600 dark:border-purple-400"
                        leftIcon={<ArrowLeft size={20} />}
                        className="focus:ring-4 focus:ring-purple-500/30"
                      >
                        {t("common.back")}
                      </Button>

                      <Button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        variant="solid"
                        size="lg"
                        rounded="xl"
                        textColor="text-white"
                        bgColor={`bg-gradient-to-r ${submitGradientClasses}`}
                        className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-purple-500/50"
                        data-testid="heygen-create-button"
                      >
                        {t("heygenVideoGenerator.createVideo")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateHeygenVideoView;
